/**
 * PageSkeleton - Componente genérico de skeleton loading para páginas
 * 
 * Suporta diferentes layouts (single, sidebar, grid) e é reutilizável
 * em qualquer página da aplicação.
 */
import React from 'react';
import { Card } from './Card';

// Componente base para linhas de skeleton
interface SkeletonLineProps {
    width?: string;
    height?: string;
    className?: string;
}

export const SkeletonLine: React.FC<SkeletonLineProps> = ({
    width = 'w-full',
    height = 'h-4',
    className = ''
}) => (
    <div className={`${width} ${height} bg-gray-200 rounded animate-pulse ${className}`} />
);

// Skeleton para cards genéricos
interface SkeletonCardProps {
    lines?: number;
    hasHeader?: boolean;
    hasActions?: boolean;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
    lines = 3,
    hasHeader = true,
    hasActions = false
}) => (
    <Card size="lg">
        {hasHeader && (
            <Card.Header>
                <SkeletonLine width="w-48" height="h-5" />
            </Card.Header>
        )}
        <Card.Content className="space-y-3">
            {Array.from({ length: lines }).map((_, i) => (
                <SkeletonLine
                    key={i}
                    width={i === lines - 1 ? 'w-2/3' : 'w-full'}
                />
            ))}
            {hasActions && (
                <div className="mt-4 flex gap-3">
                    <SkeletonLine width="w-32" height="h-10" />
                    <SkeletonLine width="w-32" height="h-10" />
                </div>
            )}
        </Card.Content>
    </Card>
);

// Tipos de layout suportados
type LayoutVariant = 'single' | 'sidebar' | 'grid';

interface PageSkeletonProps {
    /** Layout do skeleton: single (coluna única), sidebar (2/3 + 1/3), grid (colunas iguais) */
    variant?: LayoutVariant;
    /** Número de cards na área principal */
    mainCards?: number;
    /** Número de cards na sidebar (apenas para variant="sidebar") */
    sideCards?: number;
    /** Número de colunas no grid (apenas para variant="grid") */
    gridCols?: number;
    /** Classe CSS adicional */
    className?: string;
}

export const PageSkeleton: React.FC<PageSkeletonProps> = ({
    variant = 'single',
    mainCards = 2,
    sideCards = 2,
    gridCols = 3,
    className = ''
}) => {
    const renderMainCards = (count: number) => (
        <div className="space-y-6">
            {Array.from({ length: count }).map((_, i) => (
                <SkeletonCard
                    key={i}
                    lines={i === 0 ? 4 : 3}
                    hasActions={i === 0}
                />
            ))}
        </div>
    );

    const renderSideCards = (count: number) => (
        <div className="space-y-6">
            {Array.from({ length: count }).map((_, i) => (
                <SkeletonCard key={i} lines={3} />
            ))}
        </div>
    );

    // Layout de coluna única
    if (variant === 'single') {
        return (
            <div className={`flex flex-col h-full overflow-y-auto custom-scrollbar ${className}`}>
                <div className="flex-grow p-8 max-w-4xl mx-auto w-full">
                    {renderMainCards(mainCards)}
                </div>
            </div>
        );
    }

    // Layout com sidebar (2/3 + 1/3)
    if (variant === 'sidebar') {
        return (
            <div className={`flex flex-col h-full overflow-y-auto custom-scrollbar ${className}`}>
                <div className="flex-grow p-8 grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-[1600px] mx-auto w-full">
                    <div className="lg:col-span-2">
                        {renderMainCards(mainCards)}
                    </div>
                    <div>
                        {renderSideCards(sideCards)}
                    </div>
                </div>
            </div>
        );
    }

    // Layout em grid
    if (variant === 'grid') {
        const gridColsClass = {
            2: 'md:grid-cols-2',
            3: 'md:grid-cols-2 lg:grid-cols-3',
            4: 'md:grid-cols-2 lg:grid-cols-4'
        }[gridCols] || 'md:grid-cols-3';

        return (
            <div className={`flex flex-col h-full overflow-y-auto custom-scrollbar ${className}`}>
                <div className={`flex-grow p-8 grid grid-cols-1 ${gridColsClass} gap-6 max-w-[1600px] mx-auto w-full`}>
                    {Array.from({ length: mainCards }).map((_, i) => (
                        <SkeletonCard key={i} lines={3} />
                    ))}
                </div>
            </div>
        );
    }

    return null;
};

export default PageSkeleton;
